function toSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

export type AreaNeighbourhood = {
  name: string
  slug: string
}

export type AreaCity = {
  name: string
  slug: string
  live: boolean
  href: string
  neighbourhoods: AreaNeighbourhood[]
}

export type AreaRegion = {
  region: string
  slug: string
  blurb: string
  cities: AreaCity[]
}

type RawCity = { name: string; slug: string; live: boolean; neighbourhoods?: string[] }
type RawRegion = Omit<AreaRegion, 'cities'> & { cities: RawCity[] }

/**
 * Neighbourhoods registry serves THREE roles. Important to keep them
 * separate when adding entries:
 *
 *   1. Public page routing: `/service-areas/{city}/{slug}` only
 *      renders if a slug has rich content in
 *      `lib/data/neighbourhood-content.ts` (gated by housingHook).
 *      Listing a slug here does NOT spawn a thin page.
 *   2. Local Watch keyword coverage: every neighbourhood listed
 *      here is included in the parent live city's locality keyword
 *      set via `lib/local-watch/locality.ts`. Items mentioning the
 *      neighbourhood flow into that city's funnel.
 *   3. Internal linking + display: neighbourhoods can be referenced
 *      by `nearbyAreas` slugs and surfaced in city overviews.
 *
 * Be EXPANSIVE with neighbourhood entries here. Adding a slug costs
 * zero SEO and adds keyword coverage immediately. The page only
 * appears when content is written, so there's no thin-page risk.
 */
const RAW_REGIONS: RawRegion[] = [
  {
    region: 'Toronto Core',
    slug: 'toronto-core',
    blurb: 'The 416, from the downtown core through the former boroughs of Etobicoke, North York, Scarborough and East York.',
    cities: [
      {
        name: 'Toronto', slug: 'toronto', live: true,
        // Comprehensive Toronto neighbourhood list: high-search-volume
        // names across the 416 and former boroughs. Keyword coverage
        // for Local Watch matching; pages render only when content
        // is added in lib/data/neighbourhood-content.ts.
        neighbourhoods: [
          // Old-money / luxury core
          'Rosedale', 'Forest Hill', 'Lawrence Park', 'Hoggs Hollow', 'The Bridle Path',
          'Moore Park', 'Summerhill', 'Yorkville', 'The Annex', 'Cabbagetown', 'Casa Loma',
          'Lytton Park', 'Wychwood Park', 'Leaside', 'Lawrence Manor', 'Deer Park',
          'South Hill', 'Davisville Village',
          // East end
          'Riverdale', 'Leslieville', 'The Beaches', 'Greektown', 'Pape Village',
          'Corktown', 'Distillery District', 'Regent Park', 'St. Lawrence', 'Cherry Beach',
          // West end
          'High Park', 'Swansea', 'Baby Point', 'Roncesvalles', 'Parkdale',
          'Bloor West Village', 'The Junction', 'Junction Triangle', 'Dovercourt',
          // Downtown west
          'Liberty Village', 'King West', 'Queen West', 'West Queen West',
          'Trinity-Bellwoods', 'Ossington', 'Dundas West', 'Little Italy',
          'Little Portugal', 'Bloordale', 'Kensington Market', 'Chinatown',
          // Downtown core
          'Downtown', 'Entertainment District', 'Financial District', 'Bay Street Corridor',
          'CityPlace', 'Old Town',
          // Etobicoke (former town)
          'Mimico', 'New Toronto', 'Long Branch', 'Humber Bay Shores',
          // Scarborough (former town)
          'The Bluffs', 'Guildwood', 'Birchcliff', 'Cliffside', 'Agincourt',
          // North York / Don Mills (former towns)
          'Willowdale', 'Don Mills', 'Bayview Village', 'York Mills',
        ],
      },
      // The 50+ Toronto neighbourhood names live inside Toronto's
      // `neighbourhoods` array above. They are NOT separate cities.
      // Removed the duplicate city entries that previously listed them
      // alongside Toronto (Rosedale, Forest Hill, Yorkville, etc.):
      // those neighbourhood-specific routes are at
      // /service-areas/toronto/{neighbourhood} and surface via the
      // NeighbourhoodGrid on Toronto's city page.
    ],
  },
  {
    region: 'North Toronto & York Region',
    slug: 'north-gta',
    blurb: 'Executive-home corridors from North York up through Vaughan, Markham and Newmarket.',
    cities: [
      {
        name: 'York Region', slug: 'york', live: true,
        neighbourhoods: ['Markham', 'Vaughan', 'Richmond Hill', 'Thornhill', 'Aurora',
          'Newmarket', 'King City', 'Stouffville', 'Unionville', 'Kleinburg',
          'Woodbridge', 'Oak Ridges'],
      },
      {
        name: 'Thornhill', slug: 'thornhill', live: true,
        neighbourhoods: ['Vaughan Grove', 'Bayview Glen', 'Pomona Mills', 'Henderson', 'Uplands',
          'Commerce Valley', 'Thornhill Woods', 'Royal Orchard', 'Old Thornhill Village',
          'Beverley Glen', 'German Mills', 'Patterson'],
      },
      {
        name: 'Markham', slug: 'markham', live: true,
        neighbourhoods: ['Unionville', 'Cathedraltown', 'Bayview Hill', 'Bayview Glen', 'Mill Pond',
          'Cornell', 'Berczy Village', 'Victoria Square', 'Wismer', 'Swan Lake',
          'Box Grove', 'Greensborough', 'Markham Village', 'Milliken', 'Markville',
          'Buttonville', 'Angus Glen', 'Cedarwood'],
      },
      { name: 'Richmond Hill',  slug: 'richmond-hill',  live: true, neighbourhoods: ['Oak Ridges', 'Jefferson', 'Mill Pond', 'Crosby'] },
      { name: 'Vaughan',        slug: 'vaughan',         live: true, neighbourhoods: ['Woodbridge', 'Kleinburg', 'Maple', 'Concord'] },
      { name: 'Aurora',         slug: 'aurora',          live: true, neighbourhoods: ['Aurora Highlands', 'Aurora Grove', 'Hills of St Andrew', 'Bayview Wellington', 'Aurora Village'] },
      { name: 'Newmarket',      slug: 'newmarket',       live: true, neighbourhoods: ['Stonehaven', 'Summerhill Estates', 'Woodland Hill', 'Glenway', 'Armitage'] },
      { name: 'Stouffville',    slug: 'stouffville',     live: true, neighbourhoods: ['Stouffville Village', 'Ballantrae', 'Vandorf'] },
      { name: 'King City',      slug: 'king-city',       live: true, neighbourhoods: ['King City village', 'Nobleton', 'Schomberg'] },
      // North York / Willowdale / Don Mills / Unionville / Kleinburg
      // removed from this region. They're neighbourhoods of Toronto
      // (the post-amalgamation borough) and Markham, and surface on
      // those parent city pages.
    ],
  },
  {
    region: 'West GTA',
    slug: 'west-gta',
    blurb: 'Halton and Peel: Oakville lakeshore, Mississauga luxury pockets, and the Caledon estates.',
    cities: [
      {
        name: 'Peel Region', slug: 'peel', live: true,
        neighbourhoods: ['Mississauga', 'Brampton', 'Caledon', 'Port Credit', 'Lorne Park',
          'Streetsville', 'Erin Mills', 'Meadowvale', 'Bramalea', 'Heart Lake',
          'Springdale', 'Bolton', 'Caledon Village', 'Palgrave'],
      },
      {
        name: 'Halton Region', slug: 'halton', live: true,
        neighbourhoods: ['Oakville', 'Burlington', 'Milton', 'Halton Hills', 'Georgetown',
          'Acton', 'Old Oakville', 'Bronte', 'Glen Abbey', 'Aldershot',
          'Brant Hills', 'Hawthorne Village'],
      },
      {
        name: 'Mississauga', slug: 'mississauga', live: true,
        neighbourhoods: ['Port Credit', 'Lorne Park', 'Mineola', 'Cooksville', 'Erin Mills',
          'Meadowvale', 'Streetsville', 'Malton', 'Clarkson', 'Lakeview',
          'Mississauga Valleys', 'Hurontario', 'Square One', 'City Centre',
          'Sheridan', 'Sherwood Forrest', 'Churchill Meadows', 'Central Erin Mills',
          'Rathwood', 'Applewood', 'Dixie', 'Mississauga Road Corridor',
          'Credit Valley', 'East Credit'],
      },
      {
        name: 'Oakville', slug: 'oakville', live: true,
        neighbourhoods: ['Glen Abbey', 'Bronte', 'Old Oakville', 'Clearview', 'River Oaks',
          'West Oak Trails', 'Palermo', 'Iroquois Ridge', 'Joshua Creek',
          'Bronte Creek', 'College Park', 'Eastlake', 'Falgarwood', 'Morrison',
          'Westmount', 'Uptown Core', 'Glenorchy', 'Preserve'],
      },
      { name: 'Burlington',   slug: 'burlington',   live: true, neighbourhoods: ['Aldershot', 'Brant Hills', 'Tyandaga'] },
      { name: 'Brampton',     slug: 'brampton',     live: true, neighbourhoods: ['Bramalea', 'Heart Lake', 'Springdale'] },
      { name: 'Milton',       slug: 'milton',        live: true, neighbourhoods: ['Hawthorne Village', 'Scott', 'Coates', 'Beaty', 'Clarke'] },
      { name: 'Caledon',      slug: 'caledon',       live: true, neighbourhoods: ['Bolton', 'Palgrave', 'Caledon Village'] },
      { name: 'Georgetown',   slug: 'georgetown',    live: true, neighbourhoods: ['Georgetown village', 'Glen Williams', 'Acton', 'Limehouse'] },
      // Etobicoke / Lorne Park / Port Credit / Glen Abbey removed
      // from this region. They're neighbourhoods of Toronto,
      // Mississauga, and Oakville respectively and surface on those
      // parent city pages.
    ],
  },
  {
    region: 'East GTA & Durham',
    slug: 'east-gta',
    blurb: 'Lakefront Durham estates, Scarborough bluffs, and the established neighbourhoods east of the 416.',
    cities: [
      {
        name: 'Durham', slug: 'durham', live: true,
        neighbourhoods: ['Pickering', 'Ajax', 'Whitby', 'Oshawa', 'Bowmanville', 'Port Perry',
          'Brooklin', 'Bay Ridges', 'Dunbarton', 'Liverpool', 'Pickering Village',
          'Pringle Creek', 'Williamsburg', 'Northglen', 'Samac'],
      },
      // Scarborough is a Toronto borough, not a separate city. Its
      // bluff-side neighbourhoods (The Bluffs, Guildwood, Birchcliff,
      // Cliffside, Agincourt) belong on Toronto's neighbourhood list.
      { name: 'Pickering',      slug: 'pickering',      live: true, neighbourhoods: ['Bay Ridges', 'Dunbarton', 'Liverpool', 'Amberlea', 'Rougemount'] },
      { name: 'Ajax',           slug: 'ajax',            live: true, neighbourhoods: ['Pickering Village', 'Central West', 'South West', 'Westney Heights', 'Nottingham'] },
      { name: 'Whitby',         slug: 'whitby',          live: true, neighbourhoods: ['Port Whitby', 'Brooklin', 'Pringle Creek', 'Williamsburg', 'Lynde Creek'] },
      { name: 'Oshawa',         slug: 'oshawa',          live: true, neighbourhoods: ['Lakeview', 'McLaughlin', 'Northglen', 'Samac', 'Pinecrest'] },
      { name: 'Bowmanville',    slug: 'bowmanville',     live: true, neighbourhoods: ['Bowmanville village', 'Newcastle', 'Orono'] },
      { name: 'Port Perry',     slug: 'port-perry',      live: true, neighbourhoods: ['Port Perry village', 'Lake Scugog waterfront'] },
    ],
  },
  // Southwest Ontario region (Hamilton, Kitchener, Waterloo, Guelph,
  // Niagara, Cambridge, St. Catharines) was removed. Clear Guard is
  // not currently servicing that corridor and listing them as
  // upcoming areas dilutes the focused GTA + Cottage Country footprint.
  {
    region: 'Cottage Country',
    slug: 'cottage-country',
    blurb: 'Muskoka, Georgian Bay, Haliburton and Prince Edward County: second homes and waterfront estates.',
    cities: [
      {
        name: 'Cottage Country', slug: 'cottage-country', live: true,
        neighbourhoods: ['Muskoka Lakes', 'Bracebridge', 'Huntsville', 'Port Carling',
          'Gravenhurst', 'Bala', 'Collingwood', 'Blue Mountain', 'Parry Sound',
          'Haliburton', 'Prince Edward County', 'Picton', 'Wellington', 'Bloomfield',
          'Honey Harbour', 'Thornbury', 'Lake Joseph', 'Lake Rosseau'],
      },
      { name: 'Muskoka',              slug: 'muskoka',               live: true, neighbourhoods: ['Bracebridge', 'Huntsville', 'Gravenhurst', 'Port Carling', 'Bala', 'Lake Joseph', 'Lake Rosseau', 'Lake of Bays'] },
      { name: 'Huntsville',           slug: 'huntsville',            live: true, neighbourhoods: ['Huntsville town', 'Fairy Lake', 'Vernon Lake', 'Lake of Bays'] },
      { name: 'Bracebridge',          slug: 'bracebridge',           live: true, neighbourhoods: ['Bracebridge town', 'Muskoka River corridor'] },
      { name: 'Port Carling',         slug: 'port-carling',          live: true, neighbourhoods: ['Port Carling village', 'Lake Muskoka waterfront', 'Lake Rosseau waterfront'] },
      { name: 'Collingwood',          slug: 'collingwood',           live: true, neighbourhoods: ['Blue Mountain', 'Thornbury', 'Collingwood waterfront'] },
      { name: 'Blue Mountain',        slug: 'blue-mountain',         live: true, neighbourhoods: ['Blue Mountain resort village', 'Thornbury'] },
      { name: 'Parry Sound',          slug: 'parry-sound',           live: true, neighbourhoods: ['Parry Sound town', 'Georgian Bay waterfront'] },
      { name: 'Haliburton',           slug: 'haliburton',            live: true, neighbourhoods: ['Haliburton Village', 'Minden', 'Dorset'] },
      { name: 'Prince Edward County', slug: 'prince-edward-county',  live: true, neighbourhoods: ['Picton', 'Wellington', 'Bloomfield', 'Sandbanks area'] },
    ],
  },
]

export const SERVICE_REGIONS: AreaRegion[] = RAW_REGIONS.map((r) => ({
  ...r,
  cities: r.cities.map((c) => ({
    name: c.name,
    slug: c.slug,
    live: c.live,
    href: c.live ? `/service-areas/${c.slug}` : '/service-areas',
    neighbourhoods: (c.neighbourhoods ?? []).map((n) => ({
      name: n,
      slug: toSlug(n),
    })),
  })),
}))

export function getCityBySlug(citySlug: string): AreaCity | undefined {
  for (const region of SERVICE_REGIONS) {
    const city = region.cities.find((c) => c.slug === citySlug)
    if (city) return city
  }
  return undefined
}

export function getNeighbourhoodBySlug(citySlug: string, neighbourhoodSlug: string): AreaNeighbourhood | undefined {
  const city = getCityBySlug(citySlug)
  return city?.neighbourhoods.find((n) => n.slug === neighbourhoodSlug)
}

export function isNeighbourhoodSlug(citySlug: string, slug: string): boolean {
  const city = getCityBySlug(citySlug)
  return city?.neighbourhoods.some((n) => n.slug === slug) ?? false
}
